import { useState } from "react";
import { Button, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "jalali-moment";
import {
  getSaffronReport,
  updateDateSaffronReport,
} from "@services/api";
import IrrigationChart from "@/components/reports/saffron-irrigation/IrrigationChart";
import "moment/locale/fa";
import AgroMapFarm from "@/features/map/presentation/components/AgroMapFarm";
import { FieldMarkerIcon } from "@/components/map/utils/customs";
import { toolbarBaseControls } from "@/features/map/utils/configs";
import saffron from "@assets/images/saffron_Report_images/saffron.png";
import saffronLogo from "@assets/images/saffron_Report_images/saffronLogo.png";
import line from "@assets/images/harvest_date_recommendation_images/Line185.png";
import headerImage from "@assets/images/harvest_date_recommendation_images/headerImage.png";
import footerImage from "@assets/images/harvest_date_recommendation_images/footerImage.png";
import footerTitle from "@assets/images/harvest_date_recommendation_images/footerTitle.png";
import saffronReportHeader from "@assets/images/saffron_Report_images/saffronReportHeader.png";
import agroyaarPlatform from "@assets/images/harvest_date_recommendation_images/agroyaarPlatform.png";


function SaffronIrrigationReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chartHarvestData, setChartHarvestData] = useState({});

  const getSaffronReportQuery = useQuery({
    queryKey: ["getSaffronReportQuery", id],
    queryFn: () => getSaffronReport(id),
  });

  const queryClient = useQueryClient();

  let firstIrrigationDate =
    getSaffronReportQuery?.data?.firstSaffronIrrigationDateRecommendation
      ?.firstIrrigationDate;
  let lastYearFirstIrrigationDate =
    getSaffronReportQuery?.data?.firstSaffronIrrigationDateRecommendation
      ?.lastYearFirstIrrigationDate;

  let pageNumber = 0;

  const firstDate = moment(firstIrrigationDate).subtract(2, "days");

  const firstDateIso = firstDate.toISOString();

  const endDate = moment(firstIrrigationDate).add(2, "days");
  const endDataIso = endDate.toISOString();

  const firstMonth = moment(firstDateIso).locale("fa").format("MMMM");
  const endMonth = moment(endDataIso).locale("fa").format("MMMM");

  const convertDate = () => {
    if (firstMonth !== endMonth) {
      return `${+firstDate
        .locale("fa")
        .format("DD")} ${firstMonth} تا ${+endDate
        .locale("fa")
        .format("DD")} ${endMonth}  ${+endDate.locale("fa").format("YYYY")}`;
    } else {
      return `${+firstDate.locale("fa").format("DD")} تا ${+endDate
        .locale("fa")
        .format("DD")} ${endMonth} ${+endDate.locale("fa").format("YYYY")}`;
    }
  };

  const updateDate = useMutation(
    ["updateDateSaffronReport"],
    () => updateDateSaffronReport(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["getSaffronReportQuery"]);
      },
    }
  );

  const earlierOrLater = () => {
    const recomendedDate = moment(firstIrrigationDate);
    const lastYearDate = moment(lastYearFirstIrrigationDate);

    const adjustedRecomendedDate = recomendedDate.year(2000);
    const adjustedLastYearDate = lastYearDate.year(2000);

    const dayDifference = adjustedRecomendedDate.diff(
      adjustedLastYearDate,
      "days"
    );

    if (dayDifference > 0) {
      return `${dayDifference} روز دیرتر از پارسال`;
    } else if (dayDifference < 0) {
      return `${Math.abs(dayDifference)} روز زودتر از پارسال`;
    } else {
      return "مشابه پارسال";
    }
  };

  return (
    <>
      {Object.keys(chartHarvestData)?.length !== 0 ? (
        <Spin />
      ) : (
        <div className="section-container flex flex-col items-center justify-center w-[720px] mx-auto !bg-white">
          {/* Cover (Introduction) */}
          <div className="cover">
            <div className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={headerImage}
                alt="header-image"
                width="750px"
              />

              <div className="flex flex-col justify-between items-center w-full gap-8 mt-[-30px]">
                <p
                  className="text-[#222] text-[29px] font-bold text-center !font-[B-Nazanin]"
                  style={{ textShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)" }}
                >
                  کشاورزی{" "}
                  <span className="text-president !font-[B-Nazanin]">
                    مطمئن‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر، </span>
                  </span>
                  <span className="text-president !font-[B-Nazanin]">
                    دقیق‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر و </span>
                  </span>
                  <span className="text-president !font-[B-Nazanin]">
                    اقتصادی‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر</span>
                  </span>
                  <p className="text-[#000] font-semibold text-[20px] whitespace-nowrap !font-[B-Nazanin]">
                    هدف ما تصمیم‌گیری برای کشاورز نیست، کمک به تصمیم‌گیری بهتر
                    کشاورز است
                  </p>
                </p>

                <div className="flex flex-col items-center">
                  <img
                    src={saffronLogo}
                    className="w-[287px] h-[296px] !font-[B-Nazanin]"
                    alt="harvest-icon"
                  />
                  <span
                    className="text-[#5700B4] font-bold text-[40px] !font-[B-Nazanin]"
                    style={{
                      textShadow: "1px 0px 3px rgba(118, 118, 118, 0.61)",
                    }}
                  >
                    گزارش تعیین زمان آبیاری اول زعفران
                  </span>
                </div>

                <img
                  src={line}
                  alt="line"
                  width="750px"
                  className="mt-[-80px] mr-0 mb-[-45px] ml-0"
                />

                <div className="flex flex-row  justify-start items-center w-full gap-5 px-4">
                  <div className="flex flex-col justify-between items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات کشاورز
                    </span>

                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <span className="!font-[B-Nazanin]">
                          {
                            getSaffronReportQuery?.data
                              ?.firstSaffronIrrigationDateRecommendation?.farmer
                              ?.firstName
                          }{" "}
                          {
                            getSaffronReportQuery?.data
                              ?.firstSaffronIrrigationDateRecommendation?.farmer
                              ?.lastName
                          }
                        </span>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">کد کشاورزی:</span>
                          <span className="!font-sans text-[16px]">
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.farmer?.farmerCode
                            }
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">
                            استان و شهر:
                          </span>
                          <span className="!font-[B-Nazanin]">
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.farmer?.province?.name
                            }
                            ،{" "}
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.farmer?.city?.name
                            }
                          </span>
                        </div>
                      </div>

                      <img
                        src={
                          process.env.REACT_APP_ARVAN_S3_BASE_URL +
                          getSaffronReportQuery?.data
                            ?.firstSaffronIrrigationDateRecommendation?.farmer
                            ?.avatar
                        }
                        alt="profile"
                        width="70px"
                        height="70px"
                        className="rounded-full border-[2px] border-solid border-president"
                      />
                    </div>
                  </div>

                  {/* {getSaffronReportQuery?.data?.firstSaffronIrrigationDateRecommendation
                    ?.expert && (
                    <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                      <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                        اطلاعات کارشناس
                      </span>

                      <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                        <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                          <span className="!font-[B-Nazanin]">
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation?.farmer
                                ?.firstName
                            }{" "}
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation?.farmer
                                ?.lastName
                            }
                          </span>

                          <div className="flex flex-row items-start gap-2">
                            <span className="!font-[B-Nazanin]">
                              کد کارشناس:
                            </span>
                            <span className="!font-sans text-[16px]">
                              {
                                getSaffronReportQuery?.data
                                  ?.firstSaffronIrrigationDateRecommendation?.farmer
                                  ?.farmerCode
                              }
                            </span>
                          </div>

                          <div className="flex flex-row items-start gap-2">
                            <span className="!font-[B-Nazanin]">
                              استان و شهر:
                            </span>
                            <span className="!font-[B-Nazanin]">
                              {
                                getSaffronReportQuery?.data
                                  ?.firstSaffronIrrigationDateRecommendation?.farmer
                                  ?.province
                              }
                              ،{" "}
                              {
                                getSaffronReportQuery?.data
                                  ?.firstSaffronIrrigationDateRecommendation?.farmer?.city
                              }
                            </span>
                          </div>
                        </div>

                        <img
                          src={
                            process.env.REACT_APP_ARVAN_S3_BASE_URL +
                            getSaffronReportQuery?.data
                              ?.firstSaffronIrrigationDateRecommendation?.farmer?.avatar
                          }
                          alt="profile"
                          width="70px"
                          height="70px"
                          className="rounded-full border-[2px] border-solid border-president"
                        />
                      </div>
                    </div>
                  )} */}
                </div>

                <img
                  src={line}
                  alt="line"
                  width="750px"
                  className="mt-[-70px] mr-0 mb-[-45px] ml-0"
                />

                <div className="flex flex-row justify-center items-end w-full gap-7 px-4">
                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات زمین
                    </span>

                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <div className="flex flex-row items-start w-full justify-between gap-2">
                          <span className="!font-[B-Nazanin]">نام قطعه:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.season?.farm?.name
                            }
                          </span>
                        </div>

                        <div className="flex flex-row items-start w-full justify-between gap-2">
                          <span className="!font-[B-Nazanin]"> استان:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.season?.farm?.province?.name
                            }
                          </span>
                        </div>

                        <div className="flex flex-row items-start w-full justify-between gap-2">
                          <span className="!font-[B-Nazanin]">شهر:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.season?.farm?.city?.name
                            }
                          </span>
                        </div>
                      </div>

                      <div className="relative w-[80px] h-[80px] overflow-clip rounded-[999px]">
                        <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-full h-full absolute"></div>
                        <AgroMapFarm
                          mapId={
                            getSaffronReportQuery?.data
                              ?.firstSaffronIrrigationDateRecommendation?.season
                              ?.farm?.id
                          }
                          type="onlyShow"
                          toolbarOptions={toolbarBaseControls}
                          markerIcon={FieldMarkerIcon}
                          currentFarmPolygon={
                            getSaffronReportQuery?.data
                              ?.firstSaffronIrrigationDateRecommendation?.season
                              ?.farm?.polygon
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin] items-start">
                      اطلاعات فصل رشد
                    </span>
                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <div className="flex flex-row items-start w-full justify-between gap-2">
                          <span className="!font-[B-Nazanin]">نام محصول:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.season?.product?.persianName
                            }
                          </span>
                        </div>

                        <div className="flex flex-row items-start w-full justify-between gap-2 ">
                          <span className="!font-[B-Nazanin]">مساحت زمین</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.season?.area
                            }{" "}
                            هکتار
                          </span>
                        </div>

                        <div className="flex flex-row items-start w-full justify-between gap-2">
                          <span className="!font-[B-Nazanin]">فصل رشد:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              +moment(
                                getSaffronReportQuery?.data
                                  ?.firstSaffronIrrigationDateRecommendation
                                  ?.season?.growingSeason,
                                "jYYYY"
                              )
                                .locale("fa")
                                .format("YYYY")
                            }{" "}
                            {
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.season?.currentGrowingSeason?.season
                            }
                          </span>
                        </div>
                      </div>

                      <img
                        src={
                          getSaffronReportQuery?.data
                            ?.firstSaffronIrrigationDateRecommendation?.season
                            ?.product?.imageUrl
                        }
                        alt="product-name"
                        width="80px"
                        height="80px"
                        className="rounded-full"
                      />
                    </div>
                  </div>
                </div>

                {/* <img
                  src={line}
                  alt="line"
                  width="750px"
                  className="mt-[-90px] mr-0 mb-[-30px] ml-0"
                /> */}
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer-image"
                />
                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={footerTitle}
                  alt="footer-title-image"
                />
                <span className="absolute bottom-1 left-[60px] z-[3] text-[12px] font-bold text-[#fff] tracking-[9px] text-center">
                  WWW.AGROYAAR.COM
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </div>
          </div>

          {/* <!-- Page1 --> */}
          <div className="page1">
            <div className="flex flex-col justify-between items-center w-full h-[1048px]">
              <div className="flex flex-col gap-4 header w-full">
                <img
                  className="header"
                  src={saffronReportHeader}
                  alt="header-image"
                  width="750px"
                />
                <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                  WWW.AGROYAAR.COM
                </span>

                <div className="flex flex-col gap-2">
                  <h1 className="pr-8">
                    تاریخ آبیاری اول زعفران چه اهمیتی دارد؟
                  </h1>
                  <div className="flex flex-row gap-4 items-center justify-center">
                    <p className="!font-[B-Nazanin] text-justify w-[390px] text-[#333333]">
                      ارقام مختلف یک گیاه حداکثر رشد و عملکرد خود را در مجموعه
                      خاصی از عوامل محیطی دارد. هر چه سازگاری یک رقم با شرایط
                      طبیعی رشد کمتر باشد، به فعالیت­ها و عملیات زراعی بیشتری در
                      جهت افزایش عملکرد آن رقم نیاز خواهد بود. به عنوان مثال اگر
                      رقم انتخابی شما حساس به شوری خاک باشد شما باید در کنار
                      سایر مدیریت­های لازم برای محصول خود، مدیریت دقیق­تر و
                      بیشتری نسبت به شوری خاک داشته باشید تا عملکرد محصول دچار
                      افت شدید نگردد.
                    </p>
                    <img src={saffron} alt="" className="w-[240px]" />
                  </div>
                </div>

                <div className="flex flex-col border-2 w-[650px] border-[#c3aeda] my-0 mx-auto rounded-xl items-center justify-center py-2">
                  <div className="flex items-center justify-center flex-col ">
                    <div className="flex gap-2 items-center justify-center">
                      <h1 className="text-[14px]">
                        تاریخ مناسب آبیاری اول زعفران (قبل گلدهی) در قطعه زمین
                        شما :
                      </h1>
                      <span className="text-[#491086]">{convertDate()}</span>
                      <Button
                        onClick={() =>
                          updateDate.mutate({
                            id: getSaffronReportQuery?.data
                              ?.firstSaffronIrrigationDateRecommendation?.id,
                            status:
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.status,
                            farmId:
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation?.id,
                            weatherStationId:
                              getSaffronReportQuery?.data
                                .firstSaffronIrrigationDateRecommendation?.id,
                            firstIrrigationDate:
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.firstIrrigationDate,
                            firstSaffronIrrigationDateRecommendationCode:
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.firstSaffronIrrigationRequestCode,
                            createdAt:
                              getSaffronReportQuery?.data
                                ?.firstSaffronIrrigationDateRecommendation
                                ?.createdAt,
                          })
                        }
                        className="flex bg-[#491086] text-white rounded-2xl !font-[B-Nazanin] text-[11px] w-[110px] items-center justify-center"
                      >
                        بروزرسانی تاریخ
                      </Button>
                    </div>

                    <div className="flex w-[630px]">
                      <p className="!font-[B-Nazanin] text-justify text-[#333333]">
                        سامانه تعیین زمان آبیاری اول زعفران در پلتفرم اگرویار با
                        توجه به ایستگاه هواشناسی مزرعه شما، وضعیت آب و هوایی در
                        قطعه (نام قطعه) را پیش‌بینی کرده و براین اساس، تاریخ
                        مناسب آبیاری اول زعفران مزرعه شما از {convertDate()}
                        (نمودار1) بوده است.
                        <br />
                        برای افزایش دقت در برآورد محدوده زمانی مناسب، پیشنهاد
                        می‌گردد در فواصل زمانی نزدیک به زمان پیشنهاد شده توسط
                        سامانه، تاریخ پیشنهادی را بروزرسانی کنید.
                      </p>
                    </div>
                  </div>

                  <IrrigationChart
                    id={id}
                    firstDate={firstDate}
                    endDate={endDate}
                  />

                  <p className="flex justify-center !font-[B-Nazanin] text-sm text-justify text-[#333333]">
                    نمودار1 - بازه زمانی مناسب برای آبیاری اول زعفران همراه با
                    روند تغییرات دما و بارش پیش‌بینی شده در مزرعه
                  </p>
                </div>

                <div className="flex flex-col pr-7 gap-3">
                  <div className="flex flex-row gap-1">
                    <h2 className="text-[15px]">
                      مقایسه تاریخ آبیاری اول امسال با سال گذشته:{" "}
                    </h2>
                    <h2 className="text-[#491086]">{earlierOrLater()}</h2>
                  </div>
                  <p className="w-[650px] text-[#404848] text-[13px] leading-6">
                    با توجه به اطلاعات هواشناسی سال گذشته مزرعه شما، تاریخ مناسب
                    اولین آبیاری زعفران{" "}
                    <span className="text-[#491086]">{convertDate()}</span> بوده
                    است که لازم است{" "}
                    <span className="text-[#491086]">{earlierOrLater()}</span>{" "}
                    مزرعه را آبیاری نمائید.
                  </p>
                </div>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer-image"
                />

                <img
                  className="absolute bottom-2 left-[30px] z-[3]"
                  src={agroyaarPlatform}
                  alt="footer-image"
                />

                <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                  کد گزارش: {chartHarvestData?.harvestDateRecommendationCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[20px] right-[100px] max-lg:right-[32px] px-8 text-[18px]  w-[150px]"
              id="download-button"
              onClick={() => window.print()}
            >
              <p>پرینت گزارش</p>
            </Button>
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[65px] right-[100px] max-lg:right-[32px] px-8 text-[18px] w-[150px]"
              id="download-button"
              onClick={() => window.close()}
            >
              <p> بازگشت</p>
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default SaffronIrrigationReport;
